import { useEventStream } from "@/providers/EventStreamProvider";
import { useEffect } from "react";

// How often to re-send the "view" frame that keeps the backend presence marker
// alive. Must be comfortably under the marker's TTL (45s) so a delayed/dropped
// frame doesn't let presence lapse while the chat is still open.
const PRESENCE_REFRESH_INTERVAL_MS = 20_000;

/**
 * Reports "actively viewing this chat" to the backend over the event-stream
 * WebSocket, so it can suppress tool-confirmation notifications while the user
 * is looking at the chat.
 *
 * The backend has no view state: each `{type:"view", flow_session_id}` frame just
 * refreshes that chat's presence marker (a short TTL). So this hook keeps the
 * marker alive by re-sending on an interval while the chat is visible, and simply
 * stops sending when it's hidden or unmounts — the marker then expires on its
 * own. Because it's stateless per-frame, the hook can be mounted independently on
 * every visible chat surface (the workspace chat, the canvas floating chat, each
 * expanded quick-chat node) and they don't interfere.
 *
 * Visibility matters because the socket is kept open while messages stream even
 * when the tab is hidden (see EventStreamProvider), so socket liveness alone
 * would keep a backgrounded user marked as viewing — exactly when a confirmation
 * is most likely to arise. Gating the sends on `visibilitychange` avoids that.
 */
export function useChatPresence(flowSessionId: string | undefined) {
  const { sendMessage, isConnected } = useEventStream();

  useEffect(() => {
    if (!flowSessionId || !isConnected) return;

    const view = () =>
      sendMessage({ type: "view", flow_session_id: flowSessionId });

    let interval: ReturnType<typeof setInterval> | undefined;
    const start = () => {
      if (interval) return;
      view(); // mark immediately, then keep refreshing
      interval = setInterval(view, PRESENCE_REFRESH_INTERVAL_MS);
    };
    const stop = () => {
      if (interval) {
        clearInterval(interval);
        interval = undefined;
      }
    };

    if (document.visibilityState === "visible") {
      start();
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        start();
      } else {
        stop();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stop();
    };
  }, [flowSessionId, isConnected, sendMessage]);
}
